import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, PlayIcon, Terminal, Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { toastSomethingError } from '@/components/ui/toast-content';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { runSSHInstanceSSE } from '@/service/gpu-instance';
import {
  parseSSEStream,
  SSETaskIdData,
  SSEStatusData,
  SSECompleteData,
  SSEErrorData,
  SSESSHKeyData,
} from '@/utils/sse';
import { useGetServerlessInstancePricing } from '@/service/serverless-gpu/hook';
import { useGetCreditBalanceSummaryOption } from '@/service/payment/hook';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import SSHKeyModal from '../components/SSHKeyModal';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';

const GPUInstanceCreate = () => {
  const { token } = useGPUInstanceStore();
  const { workspaceId } = useParamWorkspaceId();
  const { data: instancePricing, isLoading: pricingLoading } = useGetServerlessInstancePricing();

  // Credit balance check
  const { data: creditData, isLoading: isLoadingCredit } = useQuery({
    ...useGetCreditBalanceSummaryOption(),
  });
  const totalAvailableCredit = creditData?.balances?.[0]?.available_balance?.monetary?.value
    ? Number(creditData.balances[0].available_balance.monetary.value) / 100
    : 0;
  const hasInsufficientBalance = totalAvailableCredit <= 0;

  // Form state
  const [instanceType, setInstanceType] = useState<string>('');
  const [volumeSize, setVolumeSize] = useState<number>(200);
  const [projectName, setProjectName] = useState<string>('');

  // Set default instance type to first item when pricing loads
  useEffect(() => {
    if (instancePricing && instancePricing.length > 0 && !instanceType) {
      setInstanceType(instancePricing[0].instance_type);
    }
  }, [instancePricing]);

  // SSE state
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // SSH connection info
  const [sshHost, setSSHHost] = useState<string | null>(null);
  const [sshPort, setSSHPort] = useState<number | null>(null);
  const [privateKey, setPrivateKey] = useState<string>('');
  const [showKeyModal, setShowKeyModal] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const startTimestampRef = useRef<number | null>(null);

  const handleCreate = async () => {
    if (!token) {
      toastSomethingError('Missing authentication token');
      return;
    }

    if (!instanceType) {
      toastSomethingError('Please select an instance type');
      return;
    }

    // Reset state
    setTaskId(null);
    setStatus('starting');
    setStatusMessage('Initializing instance...');
    setError(null);
    setIsRunning(true);
    setSSHHost(null);
    setSSHPort(null);
    setPrivateKey('');
    setIsReady(false);
    startTimestampRef.current = Date.now();

    try {
      const response = await runSSHInstanceSSE(
        {
          gpu_type: instanceType,
          volume_size: volumeSize,
          rootfs_size: 500, // Fixed at 500 GB
          ...(projectName.trim() && { project_name: projectName.trim() }),
        },
        token
      );

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();

      for await (const event of parseSSEStream(reader)) {
        switch (event.type) {
          case 'task_id': {
            const data = event.data as SSETaskIdData;
            setTaskId(data.task_id);
            setStatus('running');
            setStatusMessage('Instance created, waiting for SSH key...');
            break;
          }
          case 'ssh_key': {
            const data = event.data as SSESSHKeyData;
            setPrivateKey(data.private_key);
            setShowKeyModal(true);
            setStatusMessage('SSH key received, waiting for instance to be ready...');
            break;
          }
          case 'status': {
            const data = event.data as SSEStatusData;
            setStatus(data.status);
            setStatusMessage(data.message);
            break;
          }
          case 'ssh_ready': {
            setSSHHost('slintprod-1.float16.cloud');
            setSSHPort(2222);
            setIsReady(true);
            setStatusMessage('Instance ready for SSH connection');
            toast.success('Instance is ready!');
            break;
          }
          case 'complete': {
            const data = event.data as SSECompleteData;
            setStatus('completed');
            setStatusMessage(data.finish_reason || 'Session ended');
            setIsRunning(false);
            break;
          }
          case 'error': {
            const data = event.data as SSEErrorData;
            setStatus('error');
            setError(data.error);
            setStatusMessage(data.message || data.error);
            setIsRunning(false);
            toastSomethingError(data.error);
            break;
          }
        }
      }

      if (isRunning) {
        setIsRunning(false);
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Unknown error');
      setStatusMessage(err.message || 'Unknown error');
      setIsRunning(false);
      toastSomethingError(err.message);
    }
  };

  const handleCopySSHCommand = async () => {
    if (!sshHost || !sshPort || !taskId) return;
    const command = `ssh -i gpu-instance-${taskId}.pem ${taskId}@${sshHost} -p ${sshPort}`;
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const selectedPricing = instancePricing?.find(
    (p) => p.instance_type === instanceType
  );

  return (
    <div className="container py-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center">Create GPU Instance</h1>

      <div className="space-y-6">
        {/* Insufficient Balance Warning */}
        {!isLoadingCredit && hasInsufficientBalance && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Insufficient Credit Balance</AlertTitle>
            <AlertDescription className="mt-2">
              <p>
                Your current balance is {fCurrencyUSDExtended(totalAvailableCredit)}.
                Please top up your account to create GPU instances.
              </p>
              <Link to={PATH_PAGE.setting.payment} className="mt-2 inline-block">
                <Button variant="outline" size="sm" className="mt-2">
                  Go to Payment
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Configuration Form */}
        <Card>
          <CardHeader>
            <CardTitle>Instance Configuration</CardTitle>
            <CardDescription>
              Configure your GPU instance settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name (Optional)</Label>
              <Input
                id="project-name"
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. my-dev-server"
                disabled={isRunning}
              />
              <p className="text-sm text-muted-foreground">
                A friendly name for your project
              </p>
            </div>

            {/* Instance Type */}
            <div className="space-y-2">
              <Label htmlFor="instance-type">Instance Type</Label>
              <Select
                value={instanceType}
                onValueChange={setInstanceType}
                disabled={isRunning}
              >
                <SelectTrigger id="instance-type">
                  <SelectValue placeholder="Select instance type" />
                </SelectTrigger>
                <SelectContent>
                  {pricingLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : (
                    instancePricing?.map((instance) => (
                      <SelectItem
                        key={instance.instance_id}
                        value={instance.instance_type}
                      >
                        {instance.instance_type} - ${instance.price_per_hour}/hr
                        ({instance.instance_provider})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedPricing && (
                <p className="text-sm text-muted-foreground">
                  Region: {selectedPricing.instance_region} | Provider:{' '}
                  {selectedPricing.instance_provider}
                </p>
              )}
            </div>

            {/* Volume Size */}
            <div className="space-y-2">
              <Label htmlFor="volume-size">Volume Size (GB)</Label>
              <Input
                id="volume-size"
                type="number"
                value={volumeSize}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setVolumeSize(value < 200 ? 200 : value);
                }}
                min={200}
                max={10000}
                disabled={isRunning}
              />
              <p className="text-sm text-muted-foreground">
                Persistent storage for your data (200-10000 GB)
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={isRunning || !instanceType || hasInsufficientBalance}
                className="flex-1"
              >
                {isRunning ? (
                  <Loader2 className="size-4 mr-2 animate-spin" />
                ) : (
                  <PlayIcon className="size-4 mr-2" />
                )}
                {isRunning ? 'Creating...' : 'Create Instance'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        {(taskId || isRunning) && (
            <Card>
              <CardHeader>
                <CardTitle>Instance Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {taskId && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Task ID</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {taskId}
                    </code>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="font-medium capitalize">{status}</span>
                </div>
                {statusMessage && (
                  <div className="text-sm text-muted-foreground">
                    {statusMessage}
                  </div>
                )}
                {isRunning && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* SSH Connection Info */}
        {isReady && sshHost && sshPort && (
            <Card className="border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-600">
                  <Terminal className="size-5" />
                  SSH Connection Ready
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Host</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {sshHost}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Port</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {sshPort}
                    </code>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>SSH Command</Label>
                  <div className="relative">
                    <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all">
{`ssh -i gpu-instance-${taskId}.pem \\
  ${taskId}@${sshHost} -p ${sshPort}`}
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1 h-8"
                      onClick={handleCopySSHCommand}
                    >
                      {copied ? (
                        <Check className="size-4" />
                      ) : (
                        <Copy className="size-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Alert>
                  <AlertDescription>
                    Make sure you have saved your SSH private key before
                    connecting. Use <code>chmod 400</code> to set correct
                    permissions on the key file.
                  </AlertDescription>
                </Alert>

                <Alert>
                  <AlertDescription>
                    To manage or terminate this instance, go to{' '}
                    <Link to={PATH_PAGE.dashboard.gpuInstance.instances(workspaceId)}>
                      <Button variant="link" className="p-0 h-auto">GPU Instances</Button>
                    </Link>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
        )}
      </div>

      {/* SSH Key Modal */}
      <SSHKeyModal
        isOpen={showKeyModal}
        onClose={() => setShowKeyModal(false)}
        privateKey={privateKey}
        taskId={taskId || 'unknown'}
      />
    </div>
  );
};

export default GPUInstanceCreate;
